import { flow, get, property } from 'lodash';
import { routeMap } from 'router';
import { createStructuredSelector } from 'reselect';
import { posix } from 'path-parse';
import { fileKindMap } from 'modules/Taps/components/NDFL3/constants/FilesKindMap';

export const MAX_FILE_SIZE_MB = 10;
export const ALL_FILES_MAX_SIZE_MB = 20;

const ATTRIBUTES_TO_IGNORE = [
  'fileObject',
  'serverFileName',
  'sizeToDisplay',
  'fileLink',
  'fileName',
  'attributes',
  'additionalInfo',
  'name',
];

/**
 * Get all attributes from file and return dictionary result
 * Attributes is a <String>: <String> collection
 * as well as additional info
 * @param {Object} file
 */
const getDocumentAttributes = (file) => {
  const arttributes = [];

  if (file) {
    Object.keys(file).forEach((key) => {
      // Check if we got a value and the property shouldn't be filtered
      if (ATTRIBUTES_TO_IGNORE.indexOf(key) === -1 && file[key]) {
        arttributes.push({
          name: key,
          value: file[key].toString(),
        });
      }
    });
  }

  return arttributes;
};

/**
 * Create additional info object for the given file
 * @param {Object} file
 */
const createAdditionalInfo = (file) => {
  const additionalInfo = {};

  if (file) {
    Object.keys(file).forEach((key) => {
      // Check if we got a value and the property shouldn't be filtered
      if (ATTRIBUTES_TO_IGNORE.indexOf(key) === -1 && file[key]) {
        additionalInfo[key] = file[key].toString();
      }
    });
  }

  return additionalInfo;
};

/**
 * Prepare attachments for:
 * * Creating inventory
 * * Sending them to GP-3
 * * Or savin as draft on the backend
 */
const processAttachments = (files, isForInventory = true) => {
  if (!files) {
    return [];
  }
  return files.map((file) => {
    const additionalInfo = createAdditionalInfo(file);

    if (!isForInventory) {
      return Object.assign({}, file, { additionalInfo });
    }

    return {
      fileLink: file.fileLink || file.serverFileName,
      fileName: file.fileName || file.name,
      additionalInfo,
    };
  });
};

/**
 * Get max files size minus sum for all attached files including declaration in Mb
 */
const getRemainingFilesSizeMb = (files = [], maxFileSize = ALL_FILES_MAX_SIZE_MB) => {
  if (files.length === 0) {
    return maxFileSize;
  }

  const filesSize = files.reduce((prev, next) => {
    return prev + Number.parseInt(next.size || get(next, 'additionalInfo.size')) || 0;
  }, 0);

  return maxFileSize - filesSize / 1024 / 1024;
};

const getFileHref = (name) => {
  return {
    pathname: routeMap.openTapsPdf,
    query: {
      pdf: name,
    },
  };
};

/**
 * Unwrap files attributes to make a flat structure
 * to siplmify data processing and displaying
 * @param {Array} files
 */
const unwrapAttributes = (files) => {
  return files.map((file) => {
    // To prevent forEach errors
    file.attributes = file.attributes || [];
    file.additionalInfo = file.additionalInfo || {};

    file.attributes.forEach((attribute) => {
      file[attribute.name] = attribute.value;
    });

    Object.keys(file.additionalInfo).forEach((key) => {
      file[key] = file['additionalInfo'][key];
    });

    return file;
  });
};

function sumFilesSizes(files) {
  const filesSize = files.reduce((prev, next) => {
    return prev + Number.parseInt(next.size || get(next, 'additionalInfo.size')) || 0;
  }, 0);

  return filesSize / 1024 / 1024;
}

const READ_ONLY_TYPES = [fileKindMap.checks];

const getAttachInfo = createStructuredSelector({
  id: property('additionalInfo.id'),
  name: property('additionalInfo.fileName'),
  fileName: ({ fileName, additionalInfo }) =>
    additionalInfo && additionalInfo.fileName ? additionalInfo.fileName : fileName,
  fileLink: ({ fileLink, additionalInfo }) =>
    additionalInfo && additionalInfo.fileLink ? additionalInfo.fileLink : fileLink,
  size: flow(property('additionalInfo.size'), (size) => +size),
  sizeToDisplay: property('additionalInfo.sizeToDisplay'),
  description: property('additionalInfo.description'),
  serverFileName: property('additionalInfo.serverFileName'),
  status: () => 'done',
  type: flow(
    ({ fileName, additionalInfo }) =>
      additionalInfo && additionalInfo.fileName ? additionalInfo.fileName : fileName,
    (fileName) => fileName && posix(fileName).ext.slice(1),
  ),
  docType: property('additionalInfo.docType'),
  docId: property('additionalInfo.docId'),
  kind: property('additionalInfo.kind'),
  isLocked: ({ additionalInfo: { kind } = {} }) => READ_ONLY_TYPES.includes(kind),
});

const setAttachInfo = createStructuredSelector({
  id: property('id'),
  fileName: property('fileName'),
  fileLink: property('fileLink'),
  groupId: () => null,
  sign: () => null,
  additionalInfo: createStructuredSelector({
    id: property('id'),
    name: property('fileName'),
    fileName: property('fileName'),
    fileLink: property('fileLink'),
    size: property('size'),
    sizeToDisplay: property('sizeToDisplay'),
    description: property('description'),
    serverFileName: property('serverFileName'),
    docType: property('docType'),
    docId: property('docId'),
    kind: property('kind'),
  }),
});

export {
  getFileHref,
  getDocumentAttributes,
  processAttachments,
  getRemainingFilesSizeMb,
  unwrapAttributes,
  createAdditionalInfo,
  ATTRIBUTES_TO_IGNORE,
  sumFilesSizes,
  getAttachInfo,
  setAttachInfo,
};
